import { createSelector } from 'reselect';
import {
    IRestPostGroup,
    IRestPutGroup,
    restPostGroup,
    restPutGroup,
    restReadGroupsCollection,
} from '../../../services/groups';
import {
    IRestPostUser,
    IRestPutUser,
    restDeleteUser,
    restPostUser,
    restPostUserPasswordReset,
    restPutUser,
    restReadRolesCollection,
    restReadUsersCollection,
} from '../../../services/users';

import { deselectUser, resetMode } from '../../ui/usersSlice';
import { IRequestError, makeRequest } from '../sync';
import { IGroup, IRole, IUser, TOptionalNormalizedData } from './dataTypes';
import { duck } from './state';
import { TRootState } from 'src/app/store';
import {
    IRestPutUserReportAssignments,
    restPatchUserReportAssignments,
} from '@/app/services/reports';

export const setUsers = duck.defineAction<{
    collection: TOptionalNormalizedData<IUser>;
}>('SET_USERS', (state, { collection }) => ({
    ...state,
    users: collection,
}));

export const setRoles = duck.defineAction<{
    collection: TOptionalNormalizedData<IRole>;
}>('SET_ROLES', (state, { collection }) => ({
    ...state,
    roles: collection,
}));

export const setGroups = duck.defineAction<{
    collection: TOptionalNormalizedData<IGroup>;
}>('SET_GROUPS', (state, { collection }) => ({
    ...state,
    groups: collection,
}));

export default duck.getReducer();

export const getRoles = () =>
    makeRequest(
        `get:app/collections/roles`,
        () => restReadRolesCollection(),
        (dispatch, data) => dispatch(setRoles({ collection: data })),
        (dispatch, error) => undefined
    );

export const getUsers = () =>
    makeRequest(
        `get:app/collections/users`,
        () => restReadUsersCollection(),
        (dispatch, data) => dispatch(setUsers({ collection: data })),
        (dispatch, error) => undefined
    );

const groupsStorePath = 'get:app/collections/groups';

export const getGroups = () =>
    makeRequest(
        groupsStorePath,
        () => restReadGroupsCollection(),
        (dispatch, data) => dispatch(setGroups({ collection: data })),
        (dispatch, error) => undefined
    );

export const createUserStorePath = 'post:app/collections/users';

export const createUser = (user: IRestPostUser, afterRequest: () => void) =>
    makeRequest(
        createUserStorePath,
        () => restPostUser(user),
        (dispatch, data) => {
            dispatch(resetMode());
            // @ts-ignore
            dispatch(getUsers());
            if (afterRequest) {
                afterRequest();
            }
        },
        (dispatch, error) => {
            if (afterRequest) {
                afterRequest();
            }
        }
    );

export const getUpdateUserStorePath = (userId: number) =>
    `put:app/collections/users/${userId}`;

export const updateUser = (
    user: IRestPutUser,
    onSuccess?: () => void,
    onError?: (error: IRequestError) => void
) =>
    makeRequest(
        getUpdateUserStorePath(user.id),
        () => restPutUser(user),
        (dispatch) => {
            // @ts-ignore
            dispatch(getUsers());
            onSuccess?.();
        },
        (_, error) => {
            onError?.(error);
        }
    );

export const getUpdateUserReportAssignmentStorePath = (userId: number) =>
    `put:app/collections/users/${userId}/report-assignments`;

export const updateUserReportAssignments = (
    userId: number,
    assignments: IRestPutUserReportAssignments,
    onSuccess?: () => void,
    onError?: () => void
) =>
    makeRequest(
        getUpdateUserReportAssignmentStorePath(userId),
        () => restPatchUserReportAssignments(userId, assignments),
        () => {
            onSuccess?.();
        },
        () => {
            onError?.();
        }
    );

export const getPasswordResetStorePath = (userId: number) =>
    `post:app/collections/users/${userId}/password-reset`;

export const resetPassword = (userId: number, afterRequest: () => void) =>
    makeRequest(
        getPasswordResetStorePath(userId),
        () => restPostUserPasswordReset(userId),
        (dispatch, data) => {
            // @ts-ignore
            dispatch(getUsers());

            if (afterRequest) {
                afterRequest();
            }
        },
        (dispatch, error) => undefined
    );

export const getDeleteUserStorePath = (userId: number) =>
    `delete:app/collections/users/${userId}`;

export const deleteUser = (userId: number) =>
    makeRequest(
        getDeleteUserStorePath(userId),
        () => restDeleteUser(userId),
        (dispatch, data) => {
            dispatch(deselectUser());
            // @ts-ignore
            dispatch(getUsers());
        },
        (dispatch, error) => undefined
    );

export const createGroupStorePath = 'post:app/collections/groups';

export const createGroup = (
    group: IRestPostGroup,
    afterRequest?: (data: any) => void
) =>
    makeRequest(
        createGroupStorePath,
        () => restPostGroup(group),
        (dispatch, data) => {
            // @ts-ignore
            dispatch(getGroups());
            if (afterRequest) {
                afterRequest(data);
            }
        },
        (dispatch, error) => {
            if (afterRequest) {
                afterRequest(error);
            }
        }
    );

export const getUpdateGroupStorePath = (groupId: string | null) =>
    `put:app/collections/groups/${groupId}`;

export const updateGroup = (
    group: IRestPutGroup,
    afterRequest?: (data: any) => void
) =>
    makeRequest(
        getUpdateGroupStorePath(group.id),
        () => restPutGroup(group),
        (dispatch, data) => {
            // @ts-ignore
            dispatch(getGroups());
            if (afterRequest) {
                afterRequest(data);
            }
        },
        (dispatch, error) => {
            if (afterRequest) {
                afterRequest(error);
            }
        }
    );

export const getCollections = (state: TRootState) => state.app.collections;

export const getUsersCollection = createSelector(
    [getCollections],
    (collections) => collections.users
);

export const getUsersAsArray = createSelector(
    [getUsersCollection],
    (users) => users && users.result.map((id) => users.entities[id])
);
